import { useEffect, useState } from 'react';
import { createScholarship } from '../../../../services/scholarships';
import type { Scholarship, ScholarshipPurpose } from '../../../../types/scholarships';
import { TextInput } from '../../../../components/TextInput';
import { Button } from '../../../../components/Button';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';

interface AddScholarshipPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (scholarship: Scholarship) => void;
}

export const AddScholarshipPanel = ({ isOpen, onClose, onSuccess }: AddScholarshipPanelProps) => {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<ScholarshipPurpose>('Scholarship');
  const [nameError, setNameError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setPurpose('Scholarship');
      setNameError(undefined);
      setSaveError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSaving, onClose]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Roster name is required');
      return;
    }
    if (trimmed.length > 100) {
      setNameError('Roster name must be 100 characters or fewer');
      return;
    }

    setNameError(undefined);
    setIsSaving(true);
    setSaveError(null);

    try {
      const created = await createScholarship({ name: trimmed, purpose });
      onSuccess(created);
    } catch (error) {
      setSaveError(getSafeErrorMessage(error, 'Failed to create roster'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  return (
    <>
      <div
        className={`scholarship-panel-overlay ${isOpen ? 'scholarship-panel-overlay--open' : ''}`}
        onClick={handleOverlayClick}
      />
      <div className={`scholarship-panel ${isOpen ? 'scholarship-panel--open' : ''}`}>
        <div className="scholarship-panel__content">
          <div className="scholarship-panel__header">
            <h2 className="scholarship-panel__title">New roster</h2>
            <ModalCloseButton onClick={onClose} disabled={isSaving} />
          </div>

          <form
            className="scholarship-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div>
              <label className="scholarship-panel__label" htmlFor="scholarship-name">
                Name
              </label>
              <TextInput
                id="scholarship-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Fall 2026"
                error={nameError}
                disabled={isSaving}
                autoFocus
                maxLength={100}
              />
            </div>

            {/*
              The two options are named for the two sections of the roster list, because that
              is where the choice shows up: this is what decides which heading the new roster
              files under. Beyond that it only seeds which summary cards the roster starts
              with — everything stays editable afterwards, which is what the hint says so the
              choice does not feel weightier than it is. Radios rather than a select: two
              options, and the descriptions are the useful part.

              The stored values stay 'Scholarship' and 'Recruiting' — they are the API's enum,
              not display copy.
            */}
            <div>
              <span className="scholarship-panel__label" id="scholarship-purpose-label">
                What is this for?
              </span>
              <div
                className="scholarship-panel__purpose"
                role="radiogroup"
                aria-labelledby="scholarship-purpose-label"
              >
                {/*
                  The label holds only the radio and its name, so its accessible text sits at
                  the depth label-has-associated-control expects. The longer hint is a sibling
                  wired in with aria-describedby rather than nested inside the label, which is
                  what pushed the text out of reach of the rule.
                */}
                <div className="scholarship-panel__purpose-option">
                  <label htmlFor="scholarship-purpose-scholarship">
                    <input
                      id="scholarship-purpose-scholarship"
                      type="radio"
                      name="scholarship-purpose"
                      value="Scholarship"
                      checked={purpose === 'Scholarship'}
                      onChange={() => setPurpose('Scholarship')}
                      disabled={isSaving}
                      aria-describedby="scholarship-purpose-scholarship-hint"
                    />
                    Roster Management
                  </label>
                  <span
                    className="scholarship-panel__purpose-hint"
                    id="scholarship-purpose-scholarship-hint"
                  >
                    Starts with allocation, roster spots and budget cards.
                  </span>
                </div>

                <div className="scholarship-panel__purpose-option">
                  <label htmlFor="scholarship-purpose-recruiting">
                    <input
                      id="scholarship-purpose-recruiting"
                      type="radio"
                      name="scholarship-purpose"
                      value="Recruiting"
                      checked={purpose === 'Recruiting'}
                      onChange={() => setPurpose('Recruiting')}
                      disabled={isSaving}
                      aria-describedby="scholarship-purpose-recruiting-hint"
                    />
                    Recruiting Board
                  </label>
                  <span
                    className="scholarship-panel__purpose-hint"
                    id="scholarship-purpose-recruiting-hint"
                  >
                    Starts with position needs, progress and class cards.
                  </span>
                </div>
              </div>
              <p className="scholarship-panel__purpose-note">
                This sets which section the roster files under and the cards it starts with.
                You can change the cards any time from Summary.
              </p>
            </div>

            <div className="scholarship-panel__footer">
              <Button variant="primary" type="submit" disabled={isSaving} fullWidth>
                {isSaving ? 'Saving...' : 'Build roster'}
              </Button>
            </div>

            {saveError && (
              <div className="scholarship-panel__error" role="alert">{saveError}</div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};
